/**
 * Shared text measurement for column sizing.
 *
 * Both double-click auto-fit and the adaptive default layout must size columns
 * by the same rules (PM-18386: "measurable rules for ... proportional vs
 * content-based sizing"), so the measurement lives in one place.
 */

// Horizontal room a cell needs on top of its text.
export const CELL_PADDING = 24;
// Header cells additionally carry the sort icon and the resize handle.
export const HEADER_EXTRA = 40;

const FONT = '14px "Open Sans", sans-serif';
// Fallback when a 2D canvas context is unavailable (jsdom, or a browser that
// refuses the context). Approximates the average glyph width of the font above.
const FALLBACK_CHAR_PX = 7;

/**
 * Returns a `measure(value) => px` function. Create one per pass and reuse it:
 * building the canvas context is the expensive part, measuring is cheap.
 */
export const createTextMeasurer = () => {
    let ctx = null;
    try {
        ctx = document.createElement('canvas').getContext('2d');
    } catch {
        ctx = null;
    }
    if (ctx) ctx.font = FONT;

    return (value) => {
        const text = String(value ?? '');
        return ctx ? ctx.measureText(text).width : text.length * FALLBACK_CHAR_PX;
    };
};

/**
 * Only primitive cell values can be measured. Action/logo columns render React
 * elements, and those columns are excluded from adaptive sizing anyway.
 */
export const isMeasurable = (value) =>
    typeof value === 'string' || typeof value === 'number';
